import React, { useState } from 'react';
import { adminService } from '../../services/adminService';
import { DemoUserCreate } from '../../types/user';

interface DemoUserFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

const DemoUserForm: React.FC<DemoUserFormProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    plan_type: 'essential' as 'essential' | 'gold' | 'premium',
    duration_days: 365,
    notes: 'Demo user created by admin'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const createData: DemoUserCreate = {
        email: formData.email,
        full_name: formData.full_name,
        password: formData.password,
        plan_type: formData.plan_type,
        duration_days: formData.duration_days,
        notes: formData.notes
      };

      const result = await adminService.createDemoUser(createData);
      console.log('Demo user created:', result);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create demo user');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'duration_days' ? parseInt(value, 10) : value
    }));
  };

  const planDescriptions = {
    essential: 'Essential Plan - $799/year - BCP aligned with ISO 22301:2019, with incident response tools',
    gold: 'Gold Plan - $1,999/year - Essential features plus Flash RTC and dedicated onboarding',
    premium: 'Premium Plan - $3,999/year - Complete solution with executive training and a resilience badge'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Create Demo User</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="demo@example.com"
            />
          </div>

          <div>
            <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Demo User"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password *
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Minimum 8 characters"
            />
          </div>

          <div>
            <label htmlFor="plan_type" className="block text-sm font-medium text-gray-700 mb-1">
              Subscription Plan *
            </label>
            <select
              id="plan_type"
              name="plan_type"
              value={formData.plan_type}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="essential">Essential Plan ($799/year)</option>
              <option value="gold">Gold Plan ($1,999/year)</option>
              <option value="premium">Premium Plan ($3,999/year)</option>
            </select>
            <p className="text-sm text-gray-600 mt-1">
              {planDescriptions[formData.plan_type]}
            </p>
          </div>

          <div>
            <label htmlFor="duration_days" className="block text-sm font-medium text-gray-700 mb-1">
              Duration *
            </label>
            <select
              id="duration_days"
              name="duration_days"
              value={formData.duration_days}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={7}>7 days (1 week)</option>
              <option value={14}>14 days (2 weeks)</option>
              <option value={30}>30 days (1 month)</option>
              <option value={90}>90 days (3 months)</option>
              <option value={180}>180 days (6 months)</option>
              <option value={365}>365 days (1 year)</option>
              <option value={730}>730 days (2 years)</option>
              <option value={1095}>1095 days (3 years)</option>
            </select>
            <p className="text-sm text-gray-600 mt-1">
              Demo subscription will be active for {formData.duration_days} days
            </p>
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional notes about this demo user"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Demo User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DemoUserForm;
